<script setup lang="ts">
import { Enums } from "@/enums";
import { computed } from "vue";
import ProfitIcon from "~icons/solar/document-add-broken";
import { defaultResetFn } from "../../common/defaults.common";
import type { ProfitParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputCheckbox, FormInputNumeric, FormInputSelect } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = ProfitParam;
interface PropsType extends BaseProps<ModelType> {
    toggleable?: boolean;
    innerLabel?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    toggleable: false,
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => {
        return model.value.cost !== undefined && model.value.unit !== undefined;
    },
});

const actions = defineActions(model, props, state);

const collapsed = computed(() => {
    if (props.toggleable) {
        return !model.value.isActive;
    } else {
        return false;
    }
});

const prefix = computed(() => {
    if (model.value.unit === "fixed") return "Rp. ";
    return undefined;
});

const suffix = computed(() => {
    if (model.value.unit === "scale") return " %";
    return undefined;
});

const maxCost = computed(() => {
    if (model.value.unit === "scale") return 100;
    return undefined;
});
</script>

<template>
    <FormPanel :collapsed="collapsed" :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <FormInputCheckbox
                    v-if="props.toggleable"
                    v-model="model.isActive"
                    class="ml-1"
                    :disabled="!state.editMode"
                    embedMode="inline"
                />
                <ProfitIcon v-else class="ml-1" />
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-2">
                    {{ props.label ?? "Pengaturan Keuntungan" }}
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row gap-4">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInputSelect
                v-model="model.unit"
                :attachReset="defaultResetFn"
                embedMode="embed"
                :label="`Satuan ${innerLabel ?? 'Keuntungan'}`"
                :mode="state.nestMode"
                :options="Enums.ProfitUnitEnum.select"
            />
            <FormInputNumeric
                v-model="model.cost"
                :attachReset="defaultResetFn"
                :disabled="model.unit === undefined"
                embedMode="embed"
                :fraction="model.unit === 'scale'"
                :label="`Nilai ${innerLabel ?? 'Keuntungan'}`"
                :max="maxCost"
                :mode="state.nestMode"
                :prefix="prefix"
                :suffix="suffix"
            />
        </div>
    </FormPanel>
</template>
